import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { animate, stagger } from "animejs";
import {
  Disc3, Zap,
  ChevronRight, Pencil, X, Plus, Trash2,
  Camera, Image, Upload, Link2, Settings, LogOut, Shield, Search,
} from "lucide-react";
import BraceletProfileCard from "../components/BraceletProfileCard";
import ModalOverlay from "../components/ModalOverlay";
import PrivacySettingsModal from "../components/PrivacySettingsModal";
import PhotoViewerModal from "../components/PhotoViewerModal";
import PhotoFeedCard from "../components/PhotoFeedCard";
import MentionInput from "../components/MentionInput";
import UserLink from "../components/UserLink";
import parseCaption from "../utils/parseCaption";
import { KandiBeadImage, BEAD_CATALOG } from "../components/BeadSystem";
import stageTrussImg from "../assets/stage/stage-truss-tile.png";
import stageMovingHeadImg from "../assets/stage/stage-moving-head-sm.png";
import { T } from "../theme";

// ─── Design tokens ────────────────────────────────────────────────────────────

// Extended palette for the stage customizer's beam-color picker
const EDIT_COLORS = [
  "#FF1493", "#FF3CAC", "#FF006E", "#FF4500",
  "#FFA500", "#FFD700", "#ADFF2F", "#00FF00",
  "#00CED1", "#00F5FF", "#54A0FF", "#4B0082",
  "#9D4EDD", "#b44fff", "#fd79a8", "#ffeaa7",
];

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Staggered bounce across a card's mini-bead row — the bracelet reacts to touch. */
function waveBeads(cardEl) {
  if (prefersReducedMotion() || !cardEl) return;
  const beads = cardEl.querySelectorAll(".mini-bead");
  if (!beads.length) return;
  animate(beads, {
    translateY: [0, -5, 0],
    duration: 480,
    delay: stagger(32),
    ease: "inOut(2)",
    onComplete: () => beads.forEach((b) => { b.style.transform = ""; }),
  });
}

/** Neon pulse on the card whose kandi was just saved — success made visible. */
function pulseCard(kandiId) {
  if (prefersReducedMotion()) return;
  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-kandi-id="${window.CSS?.escape ? CSS.escape(kandiId) : kandiId}"]`);
    if (!el) return;
    animate(el, {
      boxShadow: [
        "0 0 0px rgba(180,79,255,0)",
        "0 0 44px rgba(180,79,255,0.8)",
        "0 4px 20px rgba(0,0,0,0.3)",
      ],
      duration: 1100,
      ease: "out(3)",
      onComplete: () => { el.style.boxShadow = ""; },
    });
  });
}

function Label({ children, style }) {
  return (
    <p style={{
      fontSize: "0.75rem", fontWeight: 700, color: T.textMuted,
      textTransform: "uppercase", letterSpacing: "0.1em",
      margin: 0, ...style,
    }}>
      {children}
    </p>
  );
}

// A strand of the app's signature bead motif standing in for a hairline rule.
const DIVIDER_BEAD_COLORS = [T.purple, T.pink, T.cyan, T.gold, T.pink, T.purple];

function Divider() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, margin: "12px 0" }}>
      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
      {DIVIDER_BEAD_COLORS.map((color, i) => (
        <div key={i} style={{
          width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
          background: `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.55) 0%, ${color} 55%)`,
          border: "1px solid rgba(255,255,255,0.25)",
          boxShadow: `0 0 6px ${color}80`,
        }} />
      ))}
      <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.06)" }} />
    </div>
  );
}

/** Mini bead row using the photorealistic generated bead images.
 *  Renders the bracelet's REAL composition — one bead per actual bead, the
 *  string sized to the bead run (a 1-bead kandi is a keepsake, not an empty
 *  progress bar), and a "+N" chip when the row is truncated. */
function MiniBeads({ colors = [], shapes = [], size = 20, count: countProp }) {
  if (!colors.length) return null;
  const count = countProp ?? Math.min(colors.length, 16);
  const rest = countProp ? 0 : colors.length - count;
  return (
    <div className="mini-beads" style={{ height: size + 6, display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
      <div style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 3 }}>
        <div style={{
          position: "absolute", left: -2, right: -2, top: "50%",
          height: 1.5, background: "rgba(255,255,255,0.1)",
          borderRadius: 2, zIndex: 0,
        }} />
        {Array.from({ length: count }, (_, i) => {
          const color = colors[i % colors.length];
          const shape = shapes[i % Math.max(shapes.length, 1)] || "pony";
          return (
            <div key={i} className="mini-bead" style={{ zIndex: 1, flexShrink: 0, display: "flex" }}>
              <KandiBeadImage shapeType={shape} color={color} size={size} />
            </div>
          );
        })}
      </div>
      {rest > 0 && (
        <span style={{
          flexShrink: 0, padding: "1px 7px", borderRadius: 10,
          background: "rgba(255,255,255,0.07)", border: T.border,
          color: T.textStrong, fontSize: "0.62rem", fontWeight: 700,
        }}>
          +{rest}
        </span>
      )}
    </div>
  );
}

/** Owners = unique humans in the journey, not raw scan events. */
function uniqueOwnerCount(history = []) {
  return new Set(history.map((h) => String(h.userId || h.username || ""))).size;
}

/**
 * Kandi names must always be fully readable — scale the font down as the name
 * gets longer (and let it wrap) instead of ellipsizing.
 */
function kandiNameSize(name = "") {
  if (name.length <= 14) return "1rem";
  if (name.length <= 22) return "0.88rem";
  if (name.length <= 32) return "0.8rem";
  return "0.72rem";
}

/** Single kandi card in the collection grid. Omit onEdit for read-only cards.
 *  The whole card opens the detail view (keyboard included); the pencil is
 *  the one nested action and stops propagation. */
function KandiCard({ kandi, onClick, onEdit }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      className="kandi-card"
      role="button"
      tabIndex={0}
      aria-label={`View ${kandi.name}`}
      data-kandi-id={kandi.id}
      onClick={() => onClick(kandi)}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
          e.preventDefault();
          onClick(kandi);
        }
      }}
      onMouseEnter={(e) => { setHov(true); waveBeads(e.currentTarget); }}
      onMouseLeave={() => setHov(false)}
      style={{
        background: T.card,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: hov ? T.borderHover : T.border,
        borderRadius: T.radius,
        padding: "18px 16px 16px",
        transform: hov ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hov
          ? "0 14px 40px rgba(180,79,255,0.22)"
          : "0 4px 20px rgba(0,0,0,0.3)",
        transition: "all 0.2s ease",
        display: "flex",
        flexDirection: "column",
        gap: 13,
        position: "relative",
        cursor: "pointer",
      }}
    >
      {/* Bead row + edit button */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <MiniBeads colors={kandi.colors || []} shapes={kandi.shapes || []} />
        </div>
        {onEdit && <button
          onClick={(e) => { e.stopPropagation(); onEdit(kandi); }}
          title="Edit beads"
          aria-label={`Edit ${kandi.name} beads`}
          className="kandi-edit-btn"
          style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: hov ? "rgba(180,79,255,0.2)" : "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: hov ? T.purple : "rgba(255,255,255,0.4)",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s",
            touchAction: "manipulation",
          }}
        >
          <Pencil size={15} />
        </button>}
      </div>

      {/* Emoji + name */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 44 }}>
        <span style={{ fontSize: "2rem", lineHeight: 1, flexShrink: 0 }} aria-hidden>
          {kandi.emoji || "✨"}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            margin: 0, fontWeight: 700, color: T.textPrimary,
            fontSize: kandiNameSize(kandi.name),
            lineHeight: 1.25, wordBreak: "break-word",
          }}>
            {kandi.name}
          </p>
          <p style={{ margin: "3px 0 0", fontSize: "0.7rem", fontFamily: "monospace", color: T.textMuted }}>
            {kandi.id}
          </p>
        </div>
        <ChevronRight size={16} color="rgba(255,255,255,0.45)" style={{ flexShrink: 0 }} />
      </div>

      {/* Mini stats */}
      <div className="kandi-stats-row" style={{ display: "flex", gap: 8 }}>
        <div style={{
          flex: 1, padding: "8px",
          background: "rgba(0,245,255,0.07)",
          borderRadius: 9, border: "1px solid rgba(0,245,255,0.15)",
          textAlign: "center",
        }}>
          <span style={{ display: "block", fontSize: "1rem", fontWeight: 800, color: T.cyan, lineHeight: 1 }}>
            {kandi.scanCount ?? 0}
          </span>
          <span style={{ fontSize: "0.7rem", color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Scans
          </span>
        </div>
        <div style={{
          flex: 1, padding: "8px",
          background: "rgba(180,79,255,0.07)",
          borderRadius: 9, border: "1px solid rgba(180,79,255,0.15)",
          textAlign: "center",
        }}>
          <span style={{ display: "block", fontSize: "1rem", fontWeight: 800, color: T.purple, lineHeight: 1 }}>
            {uniqueOwnerCount(kandi.history)}
          </span>
          <span style={{ fontSize: "0.7rem", color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Owners
          </span>
        </div>
        <div style={{
          flex: 2, padding: "8px",
          background: "rgba(255,255,255,0.03)",
          borderRadius: 9, border: T.border,
          textAlign: "center",
        }}>
          <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 600, color: T.textPrimary, lineHeight: 1 }}>
            {kandi.createdAt
              ? new Date(kandi.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })
              : "—"}
          </span>
          <span style={{ fontSize: "0.7rem", color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Created
          </span>
        </div>
      </div>
    </div>
  );
}

/** Stat card for profile section */
// ─── Stage Lights ─────────────────────────────────────────────────────────────
//
// Each beam definition:
//   color      – base neon color (hue-rotate will cycle from here through spectrum)
//   angle      – rotation in degrees from vertical (negative = lean left)
//   width      – cone width in px
//   height     – beam length in px
//   panDur     – seconds for lateral sweep cycle
//   hueDur     – seconds for full hue-rotate cycle
//   throbDur   – seconds for opacity pulse cycle
//   delay      – animation start offset (s)

const DEFAULT_BEAM_COLORS = ["#b44fff", "#ff3cac", "#00f5ff", "#ffd700"];

// Neon glow color for the avatar ring, keyed by the user's stage genre.
// No genre set → ring falls back to a cycling rainbow (see .avatar-glow-rainbow).
const GENRE_GLOW = {
  house:   "#ff9500",
  trance:  "#54a0ff",
  dnb:     "#ff3b3b",
  dubstep: "#b44fff",
  techno:  "#39ff14",
};

/** Pulsing neon ring around an avatar. Parent must be position:relative. */
function AvatarGlowRing({ color, inset = -6 }) {
  return (
    <div
      className={`avatar-glow-ring${color ? "" : " avatar-glow-rainbow"}`}
      style={{
        "--glow-color": color || T.pink,
        position: "absolute", inset,
        borderRadius: "50%",
        border: "2px solid var(--glow-color)",
        pointerEvents: "none",
      }}
    />
  );
}

function buildRigBeams(colors = DEFAULT_BEAM_COLORS, genre = null) {
  const c = [0, 1, 2, 3].map(
    (i) => colors[i % Math.max(colors.length, 1)] || DEFAULT_BEAM_COLORS[i]
  );
  const sp = genre === "dnb" ? 0.7 : genre === "dubstep" ? 1.25 : genre === "techno" ? 0.85 : genre === "trance" ? 0.95 : 1.0;
  return [
    { color: c[0], x: 10, angle: -16, width: 150, height: 460, panDur: 14 * sp, hueDur: 24 * sp, thrDur: 7.5 * sp, fixDur: 15, delay: 0,   pan: "l" },
    { color: c[1], x: 37, angle: -5,  width: 130, height: 500, panDur: 17 * sp, hueDur: 30 * sp, thrDur: 8.5 * sp, fixDur: 19, delay: 2.6, pan: "r" },
    { color: c[2], x: 63, angle: 5,   width: 130, height: 500, panDur: 15 * sp, hueDur: 27 * sp, thrDur: 9.2 * sp, fixDur: 13, delay: 4.8, pan: "l" },
    { color: c[3], x: 90, angle: 16,  width: 150, height: 460, panDur: 18 * sp, hueDur: 33 * sp, thrDur: 8 * sp,   fixDur: 17, delay: 1.7, pan: "r" },
  ];
}

/** One moving-head fixture hanging off the truss with its beam cone. */
function TrussHead({ beam }) {
  return (
    <div
      className={beam.pan === "l" ? "stage-pan-l" : "stage-pan-r"}
      style={{
        "--pan-dur": `${beam.panDur}s`,
        position: "absolute", top: 26, left: `${beam.x}%`,
        width: 0, height: 0,
        animationDelay: `${beam.delay}s`,
      }}
    >
      {/* Fixture body */}
      <img
        src={stageMovingHeadImg}
        alt=""
        style={{
          position: "absolute", top: -16, left: -23, width: 46,
          transform: `rotate(${beam.angle * 0.6}deg)`,
          transformOrigin: "50% 15%",
          filter: `drop-shadow(0 0 10px ${beam.color}55)`,
        }}
      />

      {/* Lens glow at the mouth of the fixture */}
      <div
        className="stage-fixture"
        style={{
          "--fix-dur": `${beam.fixDur}s`,
          position: "absolute", top: 22, left: -4,
          width: 8, height: 8, borderRadius: "50%",
          background: "#fff",
          boxShadow: `0 0 8px #fff, 0 0 18px ${beam.color}, 0 0 36px ${beam.color}`,
          animationDelay: `${beam.delay}s`,
        }}
      />

      {/* Beam cone — wrapped at reduced opacity so the rig stays calm */}
      <div style={{
        position: "absolute", top: 28, left: -beam.width / 2,
        width: beam.width, height: beam.height,
        transformOrigin: "50% 0%",
        transform: `rotate(${beam.angle}deg)`,
        opacity: 0.55,
      }}>
        <div
          className="stage-hue stage-throb"
          style={{
            "--hue-dur": `${beam.hueDur}s`,
            "--thr-dur": `${beam.thrDur}s`,
            position: "absolute", inset: 0,
            clipPath: "polygon(46% 0%, 54% 0%, 100% 100%, 0% 100%)",
            background: `linear-gradient(
              to bottom,
              ${beam.color}dd 0%,
              ${beam.color}77 30%,
              ${beam.color}33 60%,
              transparent 100%
            )`,
            animationDelay: `${beam.delay}s, ${beam.delay + 0.5}s`,
          }}
        />
        <div
          className="stage-throb"
          style={{
            "--thr-dur": `${beam.thrDur + 2}s`,
            position: "absolute", inset: 0,
            clipPath: "polygon(46% 0%, 54% 0%, 100% 100%, 0% 100%)",
            background: `linear-gradient(to bottom, ${beam.color}44 0%, transparent 55%)`,
            filter: "blur(14px)",
            animationDelay: `${beam.delay + 1}s`,
          }}
        />
      </div>
    </div>
  );
}

/**
 * StageRig — a lighting truss spanning the top of the stage with four
 * moving-head fixtures. Beams reuse the stage-hue/throb/pan keyframes but
 * slower, fewer and dimmer than the old 10-beam side rigs, and they're
 * anchored to visible stage hardware instead of floating at the viewport edges.
 */
function StageRig({ beams }) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 560,
        pointerEvents: "none", zIndex: 0, overflow: "visible",
      }}
    >
      {/* Truss bar — generated segment tiled across the stage width */}
      <div style={{
        position: "absolute", top: 0, left: -14, right: -14, height: 30,
        backgroundImage: `url(${stageTrussImg})`,
        backgroundSize: "auto 100%",
        backgroundRepeat: "repeat-x",
        filter: "drop-shadow(0 6px 14px rgba(0,0,0,0.55))",
        opacity: 0.95,
      }} />

      {beams.map((beam, i) => <TrussHead key={i} beam={beam} />)}
    </div>
  );
}


// ─── Edit Kandi Modal ─────────────────────────────────────────────────────────

function EditKandiModal({ kandi, onSave, onClose, saving, error }) {
  // Init beads from kandi's colors + shapes arrays
  const initBeads = () => {
    const colors = kandi.colors || [];
    const shapes = kandi.shapes || [];
    if (!colors.length) return [{ color: "#b44fff", shape: "pony" }];
    return colors.map((color, i) => ({ color, shape: shapes[i] || "pony" }));
  };

  const [beads, setBeads]             = useState(initBeads);
  // Snapshot for the dirty-check: a stray backdrop tap must never silently
  // destroy bead work
  const [initialSnapshot] = useState(() => JSON.stringify(initBeads()));
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [letterInput, setLetterInput] = useState(() => {
    const firstShape = (kandi.shapes || [])[0] || "pony";
    return firstShape.startsWith("letter-") ? firstShape.split("-")[1] : "";
  });

  const selected = beads[selectedIdx] || beads[0];

  const updateSelected = (field, value) =>
    setBeads((prev) => prev.map((b, i) => (i === selectedIdx ? { ...b, [field]: value } : b)));

  const addBead = () => {
    if (beads.length >= 16) return;
    const next = [...beads, { color: "#9D4EDD", shape: "pony" }];
    setBeads(next);
    setSelectedIdx(next.length - 1);
    setLetterInput("");
  };

  const removeBead = () => {
    if (beads.length <= 1) return;
    const next = beads.filter((_, i) => i !== selectedIdx);
    setBeads(next);
    setSelectedIdx(Math.min(selectedIdx, next.length - 1));
  };

  // Update letter input state when selecting a different bead
  const selectBead = (idx) => {
    setSelectedIdx(idx);
    const s = beads[idx]?.shape || "pony";
    setLetterInput(s.startsWith("letter-") ? s.split("-")[1] : "");
  };

  const inputBase = {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 10, color: T.textPrimary,
    fontSize: "0.95rem", outline: "none",
    fontFamily: "inherit",
  };

  const requestClose = () => {
    const dirty = JSON.stringify(beads) !== initialSnapshot;
    if (dirty && !window.confirm("Discard your bead changes? They haven't been saved.")) return;
    onClose();
  };

  return (
    <ModalOverlay
      onClose={requestClose}
      labelledBy="edit-kandi-title"
      zIndex={999}
      overlayStyle={{
        background: "rgba(10,5,21,0.88)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      cardStyle={{
        background: "rgba(14,8,32,0.98)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 24, padding: "26px 22px",
        maxWidth: 460, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 24px 64px rgba(0,0,0,0.75), 0 0 0 1px rgba(180,79,255,0.12)",
      }}
    >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <h3 id="edit-kandi-title" style={{
              margin: "0 0 4px",
              fontFamily: "var(--font-display)",
              fontSize: "1.15rem", fontWeight: 700,
              color: T.pink,
              textShadow: `0 0 18px ${T.purple}60`,
            }}>
              Edit Beads
            </h3>
            <p style={{ margin: 0, color: T.textStrong, fontSize: "0.8rem" }}>{kandi.name}</p>
          </div>
          <button
            onClick={requestClose}
            aria-label="Close bead editor"
            style={{
              width: 44, height: 44, borderRadius: "50%",
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.6)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ── Bracelet preview ── */}
        <div style={{ marginBottom: 20 }}>
          <Label style={{ marginBottom: 8 }}>Tap a bead to edit · {beads.length}/16</Label>
          <div style={{
            position: "relative", display: "flex", alignItems: "center",
            gap: 6, overflowX: "auto", padding: "10px 4px",
            scrollbarWidth: "none",
          }}>
            {/* String */}
            <div style={{
              position: "absolute", left: 4, right: 4, top: "50%",
              height: 1.5, background: "rgba(255,255,255,0.1)",
              borderRadius: 2, zIndex: 0, pointerEvents: "none",
            }} />

            {beads.map((bead, i) => (
              <button
                key={i}
                onClick={() => selectBead(i)}
                aria-label={`Bead ${i + 1} of ${beads.length}: ${bead.shape.startsWith("letter-") ? `letter ${bead.shape.split("-")[1]}` : bead.shape} bead`}
                aria-pressed={selectedIdx === i}
                style={{
                  position: "relative", zIndex: 1, flexShrink: 0,
                  background: "none", border: "none",
                  // Pad the hit area toward the 44px floor without growing the bead
                  padding: 5, margin: -2,
                  cursor: "pointer",
                  // Selection ring only when selected — keyboard focus keeps the
                  // default focus-visible outline (never remove it)
                  ...(selectedIdx === i
                    ? { outline: "2px solid rgba(255,255,255,0.7)", outlineOffset: 2 }
                    : {}),
                  borderRadius: "50%",
                  transform: selectedIdx === i ? "scale(1.18)" : "scale(1)",
                  transition: "transform 0.15s",
                }}
              >
                <KandiBeadImage shapeType={bead.shape} color={bead.color} size={32} />
              </button>
            ))}

            {/* Add bead button */}
            {beads.length < 16 && (
              <button
                onClick={addBead}
                aria-label="Add a bead"
                style={{
                  width: 44, height: 44, borderRadius: "50%", zIndex: 1, flexShrink: 0,
                  background: "rgba(255,255,255,0.05)",
                  border: "1.5px dashed rgba(255,255,255,0.22)",
                  color: "rgba(255,255,255,0.4)",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(180,79,255,0.6)"; e.currentTarget.style.color = T.purple; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.22)"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}
              >
                <Plus size={14} />
              </button>
            )}
          </div>
        </div>

        {/* ── Selected bead editor ── */}
        {selected && (
          <div style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 16, padding: "16px 14px",
            marginBottom: 20,
          }}>
            {/* Bead N of M + remove */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <Label>Bead {selectedIdx + 1} of {beads.length}</Label>
              <button
                onClick={removeBead}
                disabled={beads.length <= 1}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "4px 14px", minHeight: 44, borderRadius: 22,
                  background: `${T.red}1a`,
                  border: `1px solid ${T.red}40`,
                  color: beads.length <= 1 ? `${T.red}4d` : "#ff8080",
                  fontSize: "0.75rem", fontWeight: 600,
                  cursor: beads.length <= 1 ? "not-allowed" : "pointer",
                }}
              >
                <Trash2 size={11} /> Remove
              </button>
            </div>

            {/* Bead catalog — only real generated beads (plus letter beads) */}
            <Label style={{ marginBottom: 8 }}>Pick a Bead</Label>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 6,
            }}>
              {BEAD_CATALOG.map((entry) => {
                const isActive =
                  !selected.shape.startsWith("letter-") &&
                  selected.shape === entry.shape &&
                  (entry.shape !== "pony" ||
                    (selected.color || "").toLowerCase() === entry.color.toLowerCase());
                return (
                  <button
                    key={`${entry.shape}-${entry.color}`}
                    onClick={() => {
                      setBeads((prev) => prev.map((b, i) =>
                        i === selectedIdx ? { color: entry.color, shape: entry.shape } : b
                      ));
                      setLetterInput("");
                    }}
                    title={entry.name}
                    aria-label={entry.name}
                    aria-pressed={isActive}
                    style={{
                      height: 48, borderRadius: 10, cursor: "pointer",
                      background: isActive
                        ? "rgba(180,79,255,0.25)" : "rgba(255,255,255,0.05)",
                      border: isActive
                        ? "1px solid rgba(180,79,255,0.55)" : "1px solid rgba(255,255,255,0.08)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.15s",
                    }}
                  >
                    <KandiBeadImage shapeType={entry.shape} color={entry.color} size={30} />
                  </button>
                );
              })}

              {/* Letter bead — free text input, rendered as a classic letter tile */}
              <div style={{ position: "relative" }}>
                <input
                  maxLength={1}
                  value={letterInput}
                  onChange={(e) => {
                    const val = e.target.value.toUpperCase();
                    setLetterInput(val);
                    // Empty input reverts to a plain bead — never leave the
                    // bead wearing a letter the field no longer shows
                    updateSelected("shape", val ? `letter-${val}` : "pony");
                  }}
                  placeholder="A"
                  title="Letter bead — type any character"
                  aria-label="Letter bead — type any character"
                  style={{
                    ...inputBase,
                    width: "100%", height: 48, padding: 0,
                    textAlign: "center", fontSize: "1rem", fontWeight: 800,
                    cursor: "pointer",
                    background: selected.shape.startsWith("letter-")
                      ? "rgba(180,79,255,0.25)" : "rgba(255,255,255,0.05)",
                    border: selected.shape.startsWith("letter-")
                      ? "1px solid rgba(180,79,255,0.55)" : "1px solid rgba(255,255,255,0.12)",
                  }}
                />
                {/* Ghost hint when empty */}
                {!letterInput && (
                  <div style={{
                    position: "absolute", inset: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    pointerEvents: "none",
                  }}>
                    <KandiBeadImage shapeType="letter-A" color="rgba(255,255,255,0.2)" size={26} />
                  </div>
                )}
              </div>
            </div>
            <p style={{ fontSize: "0.72rem", color: T.textMuted, margin: "8px 0 0" }}>
              Type a letter in the last tile to make letter beads
            </p>
          </div>
        )}

        {/* ── Save error — lives inside the dialog so it can actually be seen ── */}
        {error && (
          <div role="alert" style={{
            marginBottom: 14, padding: "11px 14px",
            background: `${T.red}1f`,
            border: `1px solid ${T.red}59`,
            borderRadius: 10, color: "#ff9a9a", fontSize: "0.85rem",
          }}>
            {error}
          </div>
        )}

        {/* ── Action buttons ── */}
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={requestClose}
            style={{
              flex: 1, padding: "12px", minHeight: 44,
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 12, color: "rgba(255,255,255,0.7)",
              fontWeight: 600, fontSize: "0.9rem", cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(beads)}
            disabled={saving}
            style={{
              flex: 2, padding: "12px", minHeight: 44,
              background: "linear-gradient(135deg, #ff3cac, #b44fff)",
              border: "none", borderRadius: 12, color: "#fff",
              fontWeight: 700, fontSize: "0.9rem",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
            }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
    </ModalOverlay>
  );
}

// ─── Festival Passport ────────────────────────────────────────────────────────
// Pulls all unique locations out of kandi scan history and renders them as
// ink-stamped passport visa badges — each one slightly rotated, glowing neon.

const FESTIVAL_COLORS = [
  T.purple, T.pink, T.cyan, T.gold,
  "#ff6b6b", "#4ecdc4", "#54a0ff", "#ff9ff3",
];

// Map common festival/location keywords to a relevant icon
function festivalIcon(name = "") {
  const n = name.toLowerCase();
  if (n.includes("edc") || n.includes("electric daisy"))      return "⚡";
  if (n.includes("coachella"))                                  return "🌵";
  if (n.includes("forest") || n.includes("electric forest"))   return "🌲";
  if (n.includes("ultra"))                                      return "🔊";
  if (n.includes("burning man") || n.includes("playa"))        return "🔥";
  if (n.includes("lolla") || n.includes("lollapalooza"))       return "🎸";
  if (n.includes("bonnaroo"))                                   return "🎪";
  if (n.includes("rainbow"))                                    return "🌈";
  if (n.includes("beach") || n.includes("miami"))               return "🌊";
  if (n.includes("mountain") || n.includes("snow"))            return "🏔️";
  if (n.includes("warehouse") || n.includes("rave"))            return "🏭";
  if (n.includes("club") || n.includes("lounge"))              return "🎵";
  return "🎪";
}

function PassportStamp({ location, visitCount, firstVisit, lastVisit, color, rotation }) {
  const [hov, setHov] = useState(false);
  const icon = festivalIcon(location);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "relative",
        width: 138, flexShrink: 0,
        transform: `rotate(${rotation}deg) ${hov ? "scale(1.08)" : "scale(1)"}`,
        transition: "transform 0.2s ease",
        cursor: "default",
        zIndex: hov ? 2 : 1,
      }}
    >
      {/* Outer ring */}
      <div style={{
        borderRadius: "50%",
        border: `2.5px solid ${color}`,
        boxShadow: hov
          ? `0 0 22px ${color}99, 0 0 8px ${color}55, inset 0 0 18px ${color}22`
          : `0 0 12px ${color}55, inset 0 0 10px ${color}14`,
        padding: 3,
        transition: "box-shadow 0.2s",
      }}>
        {/* Inner ring */}
        <div style={{
          borderRadius: "50%",
          border: `1.5px dashed ${color}88`,
          padding: 8,
          background: `radial-gradient(circle at 40% 35%, ${color}18 0%, rgba(10,5,20,0.85) 70%)`,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 3,
          minHeight: 112,
        }}>
          {/* Icon */}
          <span style={{ fontSize: "1.5rem", lineHeight: 1 }}>{icon}</span>

          {/* Location name */}
          <p style={{
            margin: 0,
            color: color,
            fontSize: "0.62rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            textAlign: "center",
            lineHeight: 1.2,
            maxWidth: 80,
            textShadow: `0 0 8px ${color}`,
          }}>
            {location.length > 22 ? location.slice(0, 20) + "…" : location}
          </p>

          {/* Visit count badge */}
          <div style={{
            background: `${color}22`,
            border: `1px solid ${color}55`,
            borderRadius: 99, padding: "2px 8px",
            color: color,
            fontSize: "0.6rem", fontWeight: 700,
          }}>
            {visitCount}× visited
          </div>

          {/* Date range */}
          {firstVisit && (
            <p style={{
              margin: 0, color: "rgba(255,255,255,0.35)",
              fontSize: "0.55rem", textAlign: "center",
            }}>
              {firstVisit}{firstVisit !== lastVisit ? ` – ${lastVisit}` : ""}
            </p>
          )}
        </div>
      </div>

      {/* "STAMPED" watermark overlay */}
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        pointerEvents: "none", opacity: hov ? 0 : 0.12,
        transition: "opacity 0.2s",
      }}>
        <span style={{
          color, fontSize: "0.65rem", fontWeight: 900,
          letterSpacing: "0.35em", textTransform: "uppercase",
          transform: "rotate(-30deg)",
          border: `1.5px solid ${color}`, padding: "2px 6px",
          borderRadius: 3,
        }}>
          STAMPED
        </span>
      </div>
    </div>
  );
}

function FestivalPassport({ collection }) {
  const stamps = useMemo(() => {
    // Aggregate all scan locations from all kandi history
    const map = new Map();
    collection.forEach((kandi) => {
      (kandi.history || []).forEach((entry) => {
        const loc = (entry.location || "").trim();
        if (!loc || loc === "Unknown location" || loc === "Unknown") return;
        const existing = map.get(loc);
        const d = entry.date || entry.timestamp;
        const dateStr = d
          ? new Date(d).toLocaleDateString("en-US", { month: "short", year: "2-digit" })
          : null;
        if (existing) {
          existing.count++;
          if (dateStr) {
            if (!existing.first || dateStr < existing.first) existing.first = dateStr;
            if (!existing.last  || dateStr > existing.last)  existing.last  = dateStr;
          }
        } else {
          map.set(loc, { count: 1, first: dateStr, last: dateStr });
        }
      });
    });
    return Array.from(map.entries()).map(([loc, data], i) => ({
      location:   loc,
      visitCount: data.count,
      firstVisit: data.first,
      lastVisit:  data.last,
      color:      FESTIVAL_COLORS[i % FESTIVAL_COLORS.length],
      rotation:   (i % 2 === 0 ? 1 : -1) * (2 + (i % 3)),
    }));
  }, [collection]);

  const isEmpty = stamps.length === 0;

  return (
    <div style={{
      background: T.card, backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: T.border, borderRadius: 24,
      padding: "24px 22px",
      position: "relative", overflow: "hidden",
    }}>
      {/* Decorative passport background lines */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.03,
        backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.5) 0px, rgba(255,255,255,0.5) 1px, transparent 1px, transparent 22px)",
      }} />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, position: "relative" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: "linear-gradient(135deg, rgba(255,215,0,0.2), rgba(180,79,255,0.2))",
            border: "1px solid rgba(255,215,0,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "1.1rem",
          }}>
            🛂
          </div>
          <div>
            <p style={{
              margin: 0, fontSize: "0.72rem", fontWeight: 900, letterSpacing: "0.2em",
              textTransform: "uppercase", color: T.gold,
              textShadow: `0 0 8px ${T.gold}66`,
            }}>
              Tour Passport
            </p>
            <p style={{ margin: 0, color: T.textMuted, fontSize: "0.72rem" }}>
              {stamps.length} {stamps.length === 1 ? "stamp" : "stamps"} collected
            </p>
          </div>
        </div>

        {/* Decorative passport number */}
        <p style={{
          margin: 0, color: "rgba(255,255,255,0.1)",
          fontSize: "0.6rem", fontFamily: "monospace", letterSpacing: "0.12em",
        }}>
          KK-{String(collection.length).padStart(4, "0")}
        </p>
      </div>

      {isEmpty ? (
        /* Empty state */
        <div style={{
          textAlign: "center", padding: "26px 18px",
          border: "1.5px dashed rgba(255,215,0,0.25)",
          borderRadius: 16,
          background: "rgba(255,215,0,0.02)",
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            border: "2px dashed rgba(255,215,0,0.25)",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 14px", fontSize: "1.8rem",
          }}>
            🛂
          </div>
          <p style={{ color: T.textMuted, margin: "0 0 6px", fontWeight: 600, fontSize: "0.9rem" }}>
            No festivals yet — go PLUR!
          </p>
          <p style={{ color: "rgba(255,255,255,0.25)", margin: 0, fontSize: "0.8rem" }}>
            Scan kandi at your next event to earn your first festival stamp
          </p>
        </div>
      ) : (
        /* Stamp scroll */
        <div style={{
          display: "flex", gap: 20, flexWrap: "wrap",
          justifyContent: "center",
          padding: "8px 4px 4px",
        }}>
          {stamps.map((s) => (
            <PassportStamp key={s.location} {...s} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Kandi Orbit ──────────────────────────────────────────────────────────────
// Renders up to 8 kandi from the collection orbiting a central avatar.
// Each ring has a different speed and direction so they never sync up.

const ORBIT_RINGS = [
  { r: 68,  dur: 11, dir:  1, count: 3 },
  { r: 100, dur: 17, dir: -1, count: 3 },
  { r: 132, dur: 23, dir:  1, count: 2 },
];

function KandiOrbit({ collection, user, onKandiClick, glowColor }) {
  const slots = useMemo(() => {
    const out = [];
    let ci = 0;
    for (const ring of ORBIT_RINGS) {
      for (let i = 0; i < ring.count; i++) {
        const kandi = collection[ci % Math.max(collection.length, 1)];
        const angle = (360 / ring.count) * i;
        out.push({ ring, kandi, angle, key: `${ring.r}-${i}` });
        ci++;
      }
    }
    return out;
  }, [collection]);

  const size = 290; // container px
  const cx   = size / 2;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span style={{ fontSize: "1rem" }}>🌀</span>
        <Label>Kandi Orbit</Label>
      </div>
      <div className="orbit-stage" style={{ position: "relative", width: size, height: size, margin: "0 auto" }}>

        {/* Ring tracks */}
        {ORBIT_RINGS.map((ring) => (
          <div key={ring.r} style={{
            position: "absolute",
            top: cx - ring.r, left: cx - ring.r,
            width: ring.r * 2, height: ring.r * 2,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.07)",
            pointerEvents: "none",
          }} />
        ))}

        {/* Orbiting beads */}
        {collection.length > 0 ? slots.map(({ ring, kandi, angle, key }) => {
          const bead  = kandi || {};
          const color = (bead.colors || ["#b44fff"])[0];
          const shape = (bead.shapes || ["pony"])[0];

          return (
            <div
              key={key}
              className="orbit-spin"
              style={{
                "--orb-dur": `${ring.dur * (ring.dir === -1 ? -1 : 1)}s`,
                animationDuration: `${ring.dur}s`,
                animationDirection: ring.dir === -1 ? "reverse" : "normal",
                position: "absolute",
                top: cx - ring.r,
                left: cx - ring.r,
                width: ring.r * 2,
                height: ring.r * 2,
                borderRadius: "50%",
                transformOrigin: "50% 50%",
              }}
            >
              {/* Arm to bead position at `angle` */}
              <div style={{
                position: "absolute",
                top: "50%", left: "50%",
                width: ring.r,
                height: 0,
                transformOrigin: "0% 50%",
                transform: `rotate(${angle}deg)`,
              }}>
                {/* Bead at tip of arm */}
                <div
                  className="orbit-counter"
                  style={{
                    animationDuration: `${ring.dur}s`,
                    animationDirection: ring.dir === -1 ? "reverse" : "normal",
                    position: "absolute",
                    top: "50%",
                    left: "100%",
                    transform: "translateX(-50%) translateY(-50%)",
                    cursor: kandi ? "pointer" : "default",
                  }}
                  onClick={() => kandi && onKandiClick(kandi)}
                  title={kandi?.name || ""}
                >
                  <KandiBeadImage shapeType={shape} color={color} size={26} />
                </div>
              </div>
            </div>
          );
        }) : (
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <p style={{ color: T.textMuted, fontSize: "0.8rem", textAlign: "center", margin: 0 }}>
              Add kandi to<br />see your orbit
            </p>
          </div>
        )}

        {/* Central avatar */}
        <div style={{
          position: "absolute",
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 54, height: 54,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${T.purple}, ${T.pink})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.5rem",
          boxShadow: `0 0 24px ${T.purple}66, 0 0 8px ${T.pink}44`,
          border: "2px solid rgba(255,255,255,0.2)",
          zIndex: 2,
        }}>
          <AvatarGlowRing color={glowColor} inset={-7} />
          {user?.avatar || user?.username?.[0]?.toUpperCase() || "✨"}
        </div>
      </div>
      <p style={{ color: T.textMuted, fontSize: "0.72rem", textAlign: "center", marginTop: 10 }}>
        tap a bead to view details
      </p>
    </div>
  );
}

// ─── PLUR Meter ───────────────────────────────────────────────────────────────
// ─── Photo Upload Modal ───────────────────────────────────────────────────────

function UploadPhotoModal({ collection, friends = [], onUpload, onClose, uploading }) {
  const [file, setFile]             = useState(null);
  const [preview, setPreview]       = useState(null);
  const [message, setMessage]       = useState("");
  const [taggedKandi, setTaggedKandi] = useState(null); // full kandi object or null
  const [kandiSearch, setKandiSearch] = useState("");
  const [kandiOpen, setKandiOpen]     = useState(false);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleSubmit = () => {
    if (!file) return;
    onUpload({ file, message, kandiId: taggedKandi?.id || null });
  };

  const kandiMatches = collection.filter((k) =>
    !kandiSearch.trim() || (k.name || "").toLowerCase().includes(kandiSearch.trim().toLowerCase())
  );

  return (
    <div
      onClick={onClose}
      className="kk-modal-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="kk-modal-card"
        style={{
          width: "100%", maxWidth: 440,
          background: "rgba(16,8,32,0.96)", border: T.border,
          borderRadius: 24, padding: "28px 24px",
          maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: "1.2rem", fontWeight: 700,
            color: T.pink,
            textShadow: `0 0 18px ${T.purple}60`,
          }}>
            Share a Photo
          </h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textMuted, cursor: "pointer" }}>
            <X size={20} />
          </button>
        </div>

        {/* Image picker */}
        {!preview ? (
          <label style={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 12, padding: "40px 20px", borderRadius: 16, cursor: "pointer",
            border: "2px dashed rgba(180,79,255,0.3)",
            background: "rgba(180,79,255,0.05)",
            transition: "border-color 0.15s",
          }}>
            <Camera size={32} color={T.purple} style={{ opacity: 0.6 }} />
            <span style={{ color: T.textMuted, fontSize: "0.88rem" }}>
              Tap to choose a photo
            </span>
            <input type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
          </label>
        ) : (
          <div style={{ position: "relative", marginBottom: 16 }}>
            <img
              src={preview}
              alt="Preview"
              style={{
                width: "100%", borderRadius: 16,
                maxHeight: 280, objectFit: "cover",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            />
            <button
              onClick={() => { setFile(null); setPreview(null); }}
              style={{
                position: "absolute", top: 8, right: 8,
                width: 28, height: 28, borderRadius: "50%",
                background: "rgba(0,0,0,0.6)", border: "none",
                color: "#fff", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Message — @mentions supported */}
        <div style={{ marginTop: 16 }}>
          <Label>Message</Label>
          <MentionInput
            value={message}
            onChange={setMessage}
            friends={friends}
            placeholder="What's the vibe? Type @ to tag a friend"
            maxLength={280}
            rows={2}
            style={{ marginTop: 4 }}
          />
        </div>

        {/* Bracelet tagging — always optional */}
        <div style={{ marginTop: 14, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Label>Tag a Bracelet (optional)</Label>
            {(taggedKandi || kandiOpen) && (
              <button
                onClick={() => { setTaggedKandi(null); setKandiSearch(""); setKandiOpen(false); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: T.textMuted, fontSize: "0.72rem", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.08em", padding: "2px 4px",
                }}
              >
                Skip
              </button>
            )}
          </div>

          {taggedKandi ? (
            <div style={{
              marginTop: 6, padding: "10px 12px", borderRadius: 12,
              background: "rgba(180,79,255,0.1)", border: "1px solid rgba(180,79,255,0.35)",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: "0 0 4px", color: T.textPrimary, fontSize: "0.85rem", fontWeight: 700 }}>
                  {taggedKandi.emoji} {taggedKandi.name}
                </p>
                <MiniBeads colors={taggedKandi.colors || []} shapes={taggedKandi.shapes || []} size={14} count={10} />
              </div>
              <button
                onClick={() => { setTaggedKandi(null); setKandiSearch(""); }}
                aria-label="Remove bracelet tag"
                style={{
                  background: "rgba(255,255,255,0.06)", border: "none", borderRadius: "50%",
                  width: 26, height: 26, color: T.textMuted, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <X size={13} />
              </button>
            </div>
          ) : (
            <>
              <div style={{ position: "relative", marginTop: 6 }}>
                <Search size={14} color="rgba(255,255,255,0.3)" style={{
                  position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                }} />
                <input
                  value={kandiSearch}
                  onChange={(e) => { setKandiSearch(e.target.value); setKandiOpen(true); }}
                  onFocus={() => setKandiOpen(true)}
                  placeholder="Search your kandi..."
                  style={{
                    width: "100%", padding: "10px 14px 10px 34px",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 12, color: T.textPrimary, fontSize: "0.88rem",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>

              {kandiOpen && (
                <div style={{
                  position: "absolute", left: 0, right: 0, zIndex: 30,
                  marginTop: 6, maxHeight: 210, overflowY: "auto",
                  background: "rgba(16,8,32,0.98)",
                  border: "1px solid rgba(180,79,255,0.3)", borderRadius: 14,
                  boxShadow: "0 12px 36px rgba(0,0,0,0.55)",
                }}>
                  {kandiMatches.length === 0 ? (
                    <p style={{ margin: 0, padding: "14px 12px", color: T.textMuted, fontSize: "0.8rem", textAlign: "center" }}>
                      No kandi match "{kandiSearch}"
                    </p>
                  ) : kandiMatches.map((k) => (
                    <div
                      key={k.id}
                      onClick={() => { setTaggedKandi(k); setKandiOpen(false); }}
                      style={{
                        padding: "10px 12px", cursor: "pointer",
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      <p style={{ margin: "0 0 4px", color: T.textPrimary, fontSize: "0.84rem", fontWeight: 700 }}>
                        {k.emoji} {k.name}
                      </p>
                      <MiniBeads colors={k.colors || []} shapes={k.shapes || []} size={13} count={10} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!file || uploading}
          style={{
            width: "100%", marginTop: 20, padding: "13px 20px",
            background: !file ? "rgba(255,255,255,0.08)" : "linear-gradient(135deg, #ff3cac, #b44fff)",
            border: "none", borderRadius: 14, color: "#fff",
            fontSize: "0.95rem", fontWeight: 700, cursor: file ? "pointer" : "not-allowed",
            opacity: uploading ? 0.6 : 1,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}
        >
          <Upload size={16} />
          {uploading ? "Uploading..." : "Upload Photo"}
        </button>
      </div>
    </div>
  );
}

// ─── Photo Gallery ────────────────────────────────────────────────────────────

function PhotoGallery({ photos, apiUrl, onDelete, userId, friends = [] }) {
  const [viewerIndex, setViewerIndex] = useState(null);

  if (photos.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "30px 16px" }}>
        <Image size={32} color="rgba(255,255,255,0.1)" style={{ marginBottom: 10 }} />
        <p style={{ color: T.textMuted, margin: 0, fontSize: "0.85rem" }}>
          No photos yet — share your kandi moments!
        </p>
      </div>
    );
  }

  return (
    <>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 4, borderRadius: 12, overflow: "hidden",
      }}>
        {photos.map((photo, i) => (
          <div
            key={photo.id}
            onClick={() => setViewerIndex(i)}
            style={{
              position: "relative", aspectRatio: "1", cursor: "pointer",
              overflow: "hidden", background: "rgba(255,255,255,0.03)",
            }}
          >
            <img
              src={`${apiUrl}${photo.imageUrl}`}
              alt={photo.message || "Photo"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {(photo.message || photo.kandiName) && (
              <div style={{
                position: "absolute", bottom: 4, left: 4, right: 4,
                background: "rgba(0,0,0,0.65)", borderRadius: 6,
                padding: "3px 6px", fontSize: "0.6rem", color: "#fff",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {photo.message ? (
                  // Truncated caption preview — full caption lives in the viewer
                  parseCaption(photo.message, friends)
                ) : (
                  <>
                    <Link2 size={8} style={{ verticalAlign: -1, marginRight: 3 }} />
                    {photo.kandiName}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {viewerIndex !== null && (
        <PhotoViewerModal
          photos={photos}
          initialIndex={viewerIndex}
          apiUrl={apiUrl}
          viewerId={userId}
          friends={friends}
          onClose={() => setViewerIndex(null)}
          onDelete={onDelete ? (photoId) => { onDelete(photoId); setViewerIndex(null); } : undefined}
        />
      )}
    </>
  );
}

// ─── Stage Header ─────────────────────────────────────────────────────────────
// Spotlight wash + two beam cones hanging from the truss bottom. Reuses the
// stage-throb keyframe so it breathes with the rig.
// Glow tops sit at -6: stage-root has 36px top padding and the truss bar is
// 30px tall, so -6 relative to this header is exactly the truss bottom edge.

function StageHeader() {
  return (
    <div style={{
      position: "relative", textAlign: "center",
      minHeight: 60, marginBottom: 18, zIndex: 2,
    }}>
      {/* Spotlight wash */}
      <div
        aria-hidden
        className="stage-throb"
        style={{
          "--thr-dur": "6s",
          position: "absolute", left: "50%", top: -6,
          transform: "translateX(-50%)",
          width: "min(360px, 90%)", height: 150,
          background: `radial-gradient(ellipse at 50% 0%, ${T.purple}55 0%, ${T.pink}22 45%, transparent 75%)`,
          filter: "blur(16px)",
          pointerEvents: "none",
        }}
      />
      {/* Twin beam cones */}
      {[-1, 1].map((dir) => (
        <div
          key={dir}
          aria-hidden
          className="stage-throb"
          style={{
            "--thr-dur": "5s",
            position: "absolute", top: -6, left: "50%",
            width: 84, height: 120,
            marginLeft: -42,
            transform: `rotate(${dir * 26}deg)`,
            transformOrigin: "50% 0%",
            clipPath: "polygon(42% 0%, 58% 0%, 100% 100%, 0% 100%)",
            background: `linear-gradient(to bottom, ${dir < 0 ? T.cyan : T.pink}55, transparent 85%)`,
            pointerEvents: "none",
            animationDelay: dir < 0 ? "0s" : "1.4s",
          }}
        />
      ))}
    </div>
  );
}

// ─── Collection stats (feeds the DJ deck faders) ─────────────────────────────

function computePlurStats(collection) {
  const scans  = collection.reduce((s, k) => s + (k.scanCount || 0), 0);
  const owners = collection.reduce((s, k) => s + (k.history?.length || 0), 0);
  const trades = collection.filter(k => (k.scanCount || 0) > 0).length;
  return {
    peace:   { value: scans,             max: 50 },
    love:    { value: collection.length, max: 20 },
    unity:   { value: owners,            max: 30 },
    respect: { value: trades,            max: Math.max(collection.length, 1) },
  };
}

// ─── Now Playing ──────────────────────────────────────────────────────────────
// The user's current anthem, saved in stageConfig.nowPlaying. Spotify and
// Apple Music links render the official embedded player; anything else falls
// back to a styled card with a listen-out link.

const MUSIC_SERVICES = {
  spotify: { label: "Spotify",     color: "#1DB954" },
  apple:   { label: "Apple Music", color: "#fa2d48" },
  link:    { label: "Listen",      color: T.cyan    },
};

function parseMusicLink(url = "") {
  if (!/^https:\/\//i.test(url)) return null;
  const sp = url.match(/open\.spotify\.com\/(track|album|playlist|episode)\/([A-Za-z0-9]+)/i);
  if (sp) {
    return {
      service: "spotify",
      embedUrl: `https://open.spotify.com/embed/${sp[1].toLowerCase()}/${sp[2]}`,
      height: 152,
    };
  }
  if (/^https:\/\/(geo\.)?music\.apple\.com\//i.test(url)) {
    return {
      service: "apple",
      embedUrl: url.replace(/^https:\/\/(geo\.)?music\.apple\.com/i, "https://embed.music.apple.com"),
      height: 175,
    };
  }
  return { service: "link", embedUrl: null };
}

function NowPlayingCard({ nowPlaying, onEdit, readOnly = false }) {
  const hasTrack = !!(nowPlaying?.url || nowPlaying?.title);
  const link = hasTrack ? parseMusicLink(nowPlaying?.url || "") : null;
  const service = MUSIC_SERVICES[link?.service];

  // Nothing to show on someone else's stage if they haven't set a track
  if (readOnly && !hasTrack) return null;

  return (
    <div style={{
      background: T.card, backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: T.border, borderRadius: 24,
      padding: "22px 22px",
      position: "relative", zIndex: 2,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: "1rem" }}>🎧</span>
          <Label>Now Playing</Label>
        </div>
        {!readOnly && <button
          onClick={onEdit}
          title="Change track"
          style={{
            display: "flex", alignItems: "center", gap: 5,
            padding: "8px 14px", borderRadius: 10, minHeight: 36,
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: T.textMuted, fontSize: "0.75rem", fontWeight: 700,
            cursor: "pointer",
          }}
        >
          <Pencil size={11} /> {hasTrack ? "Change" : "Add"}
        </button>}
      </div>
      <Divider />

      {!hasTrack ? (
        <div style={{
          textAlign: "center", padding: "22px 16px",
          border: "1.5px dashed rgba(0,245,255,0.22)",
          borderRadius: 16,
          background: "rgba(0,245,255,0.02)",
        }}>
          <p style={{ color: T.textMuted, margin: "0 0 4px", fontWeight: 600, fontSize: "0.9rem" }}>
            No anthem set 🎶
          </p>
          <p style={{ color: "rgba(255,255,255,0.25)", margin: 0, fontSize: "0.8rem" }}>
            Drop a Spotify or Apple Music link so your crew knows what's spinning
          </p>
        </div>
      ) : link?.embedUrl ? (
        <iframe
          title="Now Playing"
          src={link.embedUrl}
          width="100%"
          height={link.height}
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{ borderRadius: 14, border: "none", display: "block" }}
        />
      ) : (
        <div style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 16px",
          background: "rgba(0,0,0,0.35)",
          border: "1px solid rgba(255,255,255,0.07)",
          borderRadius: 14,
        }}>
          {/* Spinning mini record */}
          <div className="jog-spin" style={{
            "--jog-dur": "6s",
            width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
            background: `conic-gradient(from 0deg, #111 0%, #2a2a2e 25%, #111 50%, #2a2a2e 75%, #111 100%)`,
            border: `2px solid ${service?.color || T.cyan}55`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.8rem",
          }}>
            🎵
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, color: T.textPrimary, fontWeight: 700, fontSize: "0.95rem",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {nowPlaying.title || "Untitled track"}
            </p>
            {nowPlaying.artist && (
              <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.78rem" }}>
                {nowPlaying.artist}
              </p>
            )}
          </div>
          {nowPlaying.url && service && (
            <a
              href={nowPlaying.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                padding: "9px 14px", borderRadius: 20, minHeight: 36,
                background: `${service.color}18`,
                border: `1px solid ${service.color}55`,
                color: service.color, fontSize: "0.75rem", fontWeight: 700,
                textDecoration: "none",
              }}
            >
              ▶ {service.label}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Recent Activity (stage feed) ─────────────────────────────────────────────

function stageTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const STAGE_ACTIVITY_META = {
  scan:          { emoji: "⚡", label: "scanned" },
  claim:         { emoji: "👑", label: "claimed" },
  register:      { emoji: "💖", label: "registered" },
  transfer:      { emoji: "🔁", label: "transferred" },
  friend_added:  { emoji: "🤝", label: "" },
  event_checkin: { emoji: "🎪", label: "checked in at" },
  photo_post:    { emoji: "📸", label: "shared a photo" },
};

function StageActivityFeed({ feed, apiUrl = "", viewerId = null, friends = [], hasMore = false, loadingMore = false, onLoadMore }) {
  // Client-side safety layer on top of server privacy filtering: restricted
  // events and events whose actor is gone (e.g. unfriended after posting)
  // are skipped silently — no error card, no placeholder.
  const isDev = process.env.NODE_ENV === "development";
  const visibleFeed = feed.filter((a) => {
    if (a?.restricted) {
      if (isDev) console.log("[stage feed] skipping restricted item:", a.id);
      return false;
    }
    if (!a?.user?.username) {
      if (isDev) console.log("[stage feed] skipping item with missing actor:", a?.id);
      return false;
    }
    return true;
  });

  return (
    <div style={{
      background: T.card, backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: T.border, borderRadius: 24,
      padding: "22px 22px",
      position: "relative", zIndex: 2,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Zap size={14} color={T.cyan} style={{ filter: `drop-shadow(0 0 6px ${T.cyan})` }} />
        <Label>Recent Activity</Label>
      </div>
      <Divider />
      {visibleFeed.length === 0 ? (
        <p style={{ color: T.textMuted, margin: 0, fontSize: "0.85rem", textAlign: "center", padding: "16px 0" }}>
          No activity yet — trade some kandi and add friends!
        </p>
      ) : (
        <>
          {visibleFeed.map((a) => {
            if (a.type === "photo_post") {
              return (
                <div key={a.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <PhotoFeedCard activity={a} apiUrl={apiUrl} viewerId={viewerId} friends={friends} />
                </div>
              );
            }
            const meta = STAGE_ACTIVITY_META[a.type] || STAGE_ACTIVITY_META.scan;
            const text = a.type === "friend_added"
              ? parseCaption(a.message)
              : a.type === "event_checkin"
                ? `${meta.label} ${a.eventName || "an event"}`
                : `${meta.label} ${a.kandiName || a.kandiId || "a kandi"}${a.location ? ` at ${a.location}` : ""}`;
            return (
              <div key={a.id} style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                padding: "10px 0",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}>
                <span style={{ fontSize: "1rem", lineHeight: 1.3, flexShrink: 0 }}>{meta.emoji}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, color: "rgba(255,255,255,0.75)", fontSize: "0.84rem", lineHeight: 1.4 }}>
                    {a.user?.username
                      ? <UserLink username={a.user.username} userId={a.user.id} />
                      : <span style={{ color: T.textPrimary, fontWeight: 700 }}>Someone</span>}{" "}
                    {text}
                  </p>
                  <p style={{ margin: "3px 0 0", color: T.textMuted, fontSize: "0.7rem" }}>
                    {stageTimeAgo(a.createdAt)}
                  </p>
                </div>
              </div>
            );
          })}

          {loadingMore && (
            <>
              <div className="feed-skeleton" style={{ height: 58, marginTop: 10 }} />
              <div className="feed-skeleton" style={{ height: 58, marginTop: 10 }} />
            </>
          )}

          {hasMore && !loadingMore && onLoadMore && (
            <button
              onClick={onLoadMore}
              style={{
                width: "100%", marginTop: 12, padding: "10px 14px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(0,245,255,0.25)",
                borderRadius: 12, color: T.cyan,
                fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              Load more
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── DJ Deck ─────────────────────────────────────────────────────────────────

function JogWheel({ size = 140, color, children, speed = 20 }) {
  const r = size / 2 - 4;
  const ticks = 32;
  return (
    <div style={{ width: size, height: size, position: "relative", margin: "0 auto" }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ position: "absolute", inset: 0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${color}44`} strokeWidth={2.5} />
        <circle cx={size/2} cy={size/2} r={r - 8} fill="rgba(0,0,0,0.6)" stroke={`${color}22`} strokeWidth={1} />
      </svg>
      <div className="jog-spin" style={{ "--jog-dur": `${speed}s`, position: "absolute", inset: 0 }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          {Array.from({ length: ticks }, (_, i) => {
            const a = (i / ticks) * Math.PI * 2;
            const x1 = size/2 + Math.cos(a) * (r - 2);
            const y1 = size/2 + Math.sin(a) * (r - 2);
            const x2 = size/2 + Math.cos(a) * (r - (i % 4 === 0 ? 14 : 8));
            const y2 = size/2 + Math.sin(a) * (r - (i % 4 === 0 ? 14 : 8));
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={`${color}${i % 4 === 0 ? "88" : "33"}`} strokeWidth={i % 4 === 0 ? 1.5 : 0.8} />;
          })}
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={2.5}
            strokeDasharray={`${Math.PI * r * 0.15} ${Math.PI * r * 0.85}`}
            style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
        </svg>
      </div>
      <div style={{
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: size * 0.42, height: size * 0.42, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(10,5,21,0.9)", border: `1px solid ${color}33`,
        zIndex: 2,
      }}>
        {children}
      </div>
    </div>
  );
}

function CDJDisplay({ lines, color = "#00ff41" }) {
  return (
    <div className="kk-readout" style={{
      background: "rgba(0,0,0,0.75)", borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.08)",
      padding: "8px 10px", marginTop: 8,
    }}>
      {lines.map((line, i) => (
        <div key={i} className={i === 0 ? "lcd-blink" : ""} style={{
          fontSize: line.size || "0.65rem", color: line.color || color,
          fontWeight: line.bold ? 800 : 500, letterSpacing: "0.04em",
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          lineHeight: 1.5,
        }}>
          {line.text}
        </div>
      ))}
    </div>
  );
}

// Pioneer-DJM-style channel fader: slotted track with side scale ticks and a
// dark rectangular fader cap that rides the channel's PLUR level.
function ChannelFader({ value, max, label }) {
  const pct = Math.min(value / Math.max(max, 1), 1);
  const trackH = 94, capH = 16;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <div style={{ position: "relative", width: 26, height: trackH }}>
        {/* Scale ticks flanking the slot */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <React.Fragment key={t}>
            <div style={{
              position: "absolute", left: 0, width: 5, height: 1,
              top: `calc(${t * 100}% - ${t}px)`,
              background: "rgba(255,255,255,0.22)",
            }} />
            <div style={{
              position: "absolute", right: 0, width: 5, height: 1,
              top: `calc(${t * 100}% - ${t}px)`,
              background: "rgba(255,255,255,0.22)",
            }} />
          </React.Fragment>
        ))}
        {/* Fader slot */}
        <div style={{
          position: "absolute", left: "50%", top: 0, bottom: 0,
          width: 3, marginLeft: -1.5, borderRadius: 2,
          background: "rgba(0,0,0,0.85)",
          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.9), 0 1px 0 rgba(255,255,255,0.05)",
        }} />
        {/* Fader cap */}
        <div style={{
          position: "absolute", left: 1, right: 1,
          bottom: pct * (trackH - capH),
          height: capH, borderRadius: 3,
          background: "linear-gradient(180deg, #3c3c44 0%, #23232a 45%, #101015 100%)",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "0 2px 6px rgba(0,0,0,0.65)",
          transition: "bottom 0.6s ease",
        }}>
          <div style={{
            position: "absolute", top: "50%", left: 3, right: 3,
            height: 2, marginTop: -1, borderRadius: 1,
            background: "#e8e8e8",
          }} />
        </div>
      </div>
      <span className="kk-readout" style={{
        fontSize: "0.6rem", fontWeight: 700, color: T.textMuted,
        letterSpacing: "0.04em",
      }}>
        {label}
      </span>
    </div>
  );
}

function VUMeter({ value, max, color }) {
  const bars = 8;
  const filled = Math.round((Math.min(value, max) / Math.max(max, 1)) * bars);
  return (
    <div style={{ display: "flex", flexDirection: "column-reverse", gap: 2, alignItems: "center" }}>
      {Array.from({ length: bars }, (_, i) => (
        <div key={i} style={{
          width: 16, height: 5, borderRadius: 1,
          background: i < filled
            ? i >= bars - 2 ? "#ff4444" : i >= bars - 4 ? "#ffaa00" : color
            : "rgba(255,255,255,0.06)",
          transition: "background 0.3s",
          boxShadow: i < filled ? `0 0 4px ${i >= bars - 2 ? "#ff4444" : color}44` : "none",
        }} />
      ))}
    </div>
  );
}

function DJDeck({ user, kandiCount, totalScans, friendCount, collection, onKandiClick, glowColor }) {
  const plurStats = useMemo(() => computePlurStats(collection), [collection]);

  const bpm = Math.round(Math.min(Math.max(totalScans * 2.5, 80), 180));
  const latestKandi = collection[0];
  const jogSpeed = Math.max(8, 24 - collection.length * 0.8);

  return (
    <div style={{
      background: "linear-gradient(180deg, rgba(15,10,25,0.95) 0%, rgba(8,5,18,0.98) 100%)",
      border: "1px solid rgba(255,255,255,0.12)",
      borderRadius: 24, padding: "26px 24px",
      position: "relative", overflow: "hidden",
      boxShadow: `0 0 60px ${T.purple}26, 0 24px 60px rgba(0,0,0,0.45)`,
    }}>
      {/* Deck surface texture */}
      <div style={{
        position: "absolute", inset: 0, borderRadius: 24,
        background: "repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.008) 2px, rgba(255,255,255,0.008) 3px)",
        pointerEvents: "none",
      }} />

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        gap: 18, alignItems: "start", position: "relative", zIndex: 1,
        maxWidth: 880, margin: "0 auto",
      }}>
        {/* ── Left CDJ ── */}
        <div style={{ textAlign: "center" }}>
          <JogWheel size={170} color={T.purple} speed={jogSpeed}>
            <div style={{
              position: "relative",
              width: 60, height: 60, borderRadius: "50%",
              background: `linear-gradient(135deg, ${T.purple}, ${T.pink})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.25rem", fontWeight: 800, color: "#fff",
              boxShadow: `0 0 18px ${T.purple}66`,
              border: "2px solid rgba(255,255,255,0.18)",
            }}>
              <AvatarGlowRing color={glowColor} inset={-5} />
              {user.username.slice(0, 2).toUpperCase()}
            </div>
          </JogWheel>
          <CDJDisplay lines={[
            { text: user.username.toUpperCase(), bold: true, size: "0.82rem" },
            { text: `LIVE SINCE: ${user.joinedDate ? new Date(user.joinedDate).toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase() : "???"}`, color: "#00ff4188", size: "0.72rem" },
            { text: user.bio || "PLUR VIBES ONLY", color: "#00ff4155", size: "0.66rem" },
          ]} />
        </div>

        {/* ── Mixer ── */}
        <div style={{
          minWidth: 124, padding: "10px 8px",
          background: "rgba(0,0,0,0.3)", borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.06)",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
        }}>
          {/* BPM display */}
          <div style={{
            background: "rgba(0,0,0,0.8)", borderRadius: 6,
            padding: "6px 10px", textAlign: "center", width: "100%",
            border: "1px solid rgba(255,255,255,0.08)",
          }}>
            <div className="kk-readout" style={{
              fontSize: "1.6rem",
              fontWeight: 700, color: "#ff3333", letterSpacing: "0.02em",
              textShadow: "0 0 10px rgba(255,51,51,0.6)",
            }}>
              {bpm}
            </div>
            <div style={{ fontSize: "0.5rem", color: T.textMuted, fontWeight: 700, letterSpacing: "0.12em" }}>
              BPM
            </div>
          </div>

          {/* Channel faders (PLUR) */}
          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
            <ChannelFader value={plurStats.peace.value}   max={plurStats.peace.max}   label="P" />
            <ChannelFader value={plurStats.love.value}    max={plurStats.love.max}    label="L" />
            <ChannelFader value={plurStats.unity.value}   max={plurStats.unity.max}   label="U" />
            <ChannelFader value={plurStats.respect.value} max={plurStats.respect.max} label="R" />
          </div>

          {/* VU meter + friend count */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <VUMeter value={kandiCount} max={20} color={T.cyan} />
            <div style={{ textAlign: "center" }}>
              <div className="kk-readout" style={{
                fontSize: "0.9rem",
                fontWeight: 700, color: T.pink,
              }}>
                {friendCount}
              </div>
              <div style={{ fontSize: "0.45rem", color: T.textMuted, fontWeight: 600, letterSpacing: "0.1em" }}>
                CREW
              </div>
            </div>
            <VUMeter value={totalScans} max={50} color={T.purple} />
          </div>

          {/* Crossfader */}
          <div style={{ width: "100%", padding: "0 4px" }}>
            <div style={{
              height: 6, borderRadius: 3,
              background: "rgba(255,255,255,0.06)", position: "relative",
              border: "1px solid rgba(255,255,255,0.08)",
            }}>
              <div style={{
                position: "absolute", top: -3, height: 12, width: 18,
                borderRadius: 3, background: "rgba(200,200,200,0.9)",
                border: "1px solid rgba(255,255,255,0.3)",
                left: `${Math.min(((plurStats.peace.value / plurStats.peace.max +
                  plurStats.love.value / plurStats.love.max +
                  plurStats.unity.value / plurStats.unity.max +
                  plurStats.respect.value / plurStats.respect.max) / 4) * 100, 100) * 0.8 + 2}%`,
                transform: "translateX(-50%)",
                boxShadow: "0 0 4px rgba(255,255,255,0.3)",
              }} />
            </div>
          </div>
        </div>

        {/* ── Right CDJ ── */}
        <div style={{ textAlign: "center" }}>
          <JogWheel size={170} color={T.cyan} speed={jogSpeed * 0.8}>
            {latestKandi ? (
              <div
                onClick={() => latestKandi && onKandiClick(latestKandi)}
                style={{ cursor: latestKandi ? "pointer" : "default", fontSize: "2rem" }}
              >
                {latestKandi.emoji || "✨"}
              </div>
            ) : (
              <Disc3 size={28} color={T.textMuted} />
            )}
          </JogWheel>
          <CDJDisplay lines={[
            { text: latestKandi ? latestKandi.name.toUpperCase() : "NO TRACKS", bold: true, size: "0.82rem", color: "#00f5ff" },
            { text: `${kandiCount} TRACK${kandiCount !== 1 ? "S" : ""} LOADED`, color: "#00f5ff88", size: "0.72rem" },
            { text: `${totalScans} PLAYS`, color: "#00f5ff55", size: "0.66rem" },
          ]} />
        </div>
      </div>
    </div>
  );
}

// ─── Stage Customizer Modal ──────────────────────────────────────────────────

const GENRE_OPTIONS = [
  { key: "house",   label: "House",   emoji: "🏠" },
  { key: "trance",  label: "Trance",  emoji: "🌀" },
  { key: "dnb",     label: "D&B",     emoji: "🥁" },
  { key: "dubstep", label: "Dubstep", emoji: "💥" },
  { key: "techno",  label: "Techno",  emoji: "⚡" },
];

function StageCustomizer({ stageConfig, onSave, onClose, saving }) {
  const [colors, setColors]       = useState(stageConfig?.beamColors || DEFAULT_BEAM_COLORS);
  const [genre, setGenre]         = useState(stageConfig?.genre || null);
  const [editIdx, setEditIdx]     = useState(null);
  const [song, setSong]           = useState({
    title:  stageConfig?.nowPlaying?.title  || "",
    artist: stageConfig?.nowPlaying?.artist || "",
    url:    stageConfig?.nowPlaying?.url    || "",
  });

  // Normalize pasted links so parseMusicLink / the backend accept them
  const handleSongUrl = (raw) => {
    let url = raw.trim();
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`;
    url = url.replace(/^http:\/\//i, "https://");
    setSong((s) => ({ ...s, url }));
  };

  const songLink = song.url ? parseMusicLink(song.url) : null;
  const songService = MUSIC_SERVICES[songLink?.service];

  const songInputStyle = {
    width: "100%", padding: "11px 14px", boxSizing: "border-box",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12, color: T.textPrimary, fontSize: "0.88rem",
    outline: "none", fontFamily: "inherit",
  };

  const handleColorChange = (idx, color) => {
    const next = [...colors];
    next[idx] = color;
    setColors(next);
    setEditIdx(null);
  };

  const addSlot = () => { if (colors.length < 4) setColors([...colors, "#b44fff"]); };
  const removeSlot = (idx) => { if (colors.length > 2) setColors(colors.filter((_, i) => i !== idx)); };

  return (
    <div className="kk-modal-overlay" style={{
      position: "fixed", inset: 0, zIndex: 999,
      background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }} onClick={onClose}>
      <div className="kk-modal-card" style={{
        background: "linear-gradient(160deg, #0a0515, #0a0a0f)",
        border: "1px solid rgba(255,255,255,0.12)", borderRadius: 24,
        padding: "28px 24px", maxWidth: 400, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: T.textPrimary }}>Stage Settings</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
            <X size={18} color={T.textMuted} />
          </button>
        </div>

        {/* Beam colors */}
        <Label style={{ marginBottom: 10 }}>Light Colors</Label>
        <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          {colors.map((c, i) => (
            <div key={i} style={{ position: "relative" }}>
              <div
                onClick={() => setEditIdx(editIdx === i ? null : i)}
                style={{
                  width: 40, height: 40, borderRadius: "50%", cursor: "pointer",
                  background: c, border: editIdx === i ? "2px solid #fff" : "2px solid rgba(255,255,255,0.15)",
                  boxShadow: `0 0 14px ${c}66`,
                  transition: "all 0.2s",
                }}
              />
              {colors.length > 2 && (
                <button onClick={() => removeSlot(i)} style={{
                  position: "absolute", top: -6, right: -6, width: 16, height: 16,
                  borderRadius: "50%", background: "rgba(255,68,68,0.8)", border: "none",
                  color: "#fff", fontSize: "0.55rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>×</button>
              )}
            </div>
          ))}
          {colors.length < 4 && (
            <div
              onClick={addSlot}
              style={{
                width: 40, height: 40, borderRadius: "50%", cursor: "pointer",
                border: "2px dashed rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Plus size={14} color={T.textMuted} />
            </div>
          )}
        </div>

        {/* Color picker grid */}
        {editIdx !== null && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6,
            padding: 10, background: "rgba(0,0,0,0.3)", borderRadius: 12,
            marginBottom: 12, border: "1px solid rgba(255,255,255,0.06)",
          }}>
            {EDIT_COLORS.map(c => (
              <div
                key={c}
                onClick={() => handleColorChange(editIdx, c)}
                style={{
                  width: 28, height: 28, borderRadius: 6, cursor: "pointer",
                  background: c, border: colors[editIdx] === c ? "2px solid #fff" : "2px solid transparent",
                  transition: "border 0.15s",
                }}
              />
            ))}
          </div>
        )}

        {/* Genre */}
        <Label style={{ marginBottom: 10, marginTop: 16 }}>Genre / Vibe</Label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
          {GENRE_OPTIONS.map(g => (
            <button
              key={g.key}
              onClick={() => setGenre(genre === g.key ? null : g.key)}
              style={{
                padding: "8px 14px", borderRadius: 20, cursor: "pointer",
                fontSize: "0.78rem", fontWeight: 600,
                background: genre === g.key ? "linear-gradient(135deg, rgba(180,79,255,0.3), rgba(255,60,172,0.3))" : "rgba(255,255,255,0.04)",
                border: genre === g.key ? "1px solid rgba(180,79,255,0.5)" : "1px solid rgba(255,255,255,0.08)",
                color: genre === g.key ? T.textPrimary : T.textMuted,
                transition: "all 0.2s",
              }}
            >
              {g.emoji} {g.label}
            </button>
          ))}
        </div>

        {/* Preview strip */}
        <div style={{
          display: "flex", gap: 4, justifyContent: "center", marginBottom: 20,
          padding: 12, borderRadius: 12, background: "rgba(0,0,0,0.4)",
        }}>
          {colors.map((c, i) => (
            <div key={i} style={{
              flex: 1, height: 24, borderRadius: 4,
              background: `linear-gradient(to top, ${c}, ${c}44)`,
              boxShadow: `0 0 12px ${c}44`,
            }} />
          ))}
        </div>

        {/* Now Playing */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <Label>Now Playing 🎧</Label>
          {song.url && songService && (
            <span style={{
              fontSize: "0.68rem", fontWeight: 800,
              padding: "3px 10px", borderRadius: 20,
              background: `${songService.color}18`,
              border: `1px solid ${songService.color}55`,
              color: songService.color,
            }}>
              {songLink?.service === "link" ? "Custom link" : songService.label}
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
          <input
            value={song.url}
            onChange={(e) => handleSongUrl(e.target.value)}
            placeholder="Paste a Spotify or Apple Music link"
            style={songInputStyle}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={song.title}
              onChange={(e) => setSong((s) => ({ ...s, title: e.target.value.slice(0, 120) }))}
              placeholder="Song title"
              style={{ ...songInputStyle, flex: 1.4 }}
            />
            <input
              value={song.artist}
              onChange={(e) => setSong((s) => ({ ...s, artist: e.target.value.slice(0, 120) }))}
              placeholder="Artist"
              style={{ ...songInputStyle, flex: 1 }}
            />
          </div>
          {(song.url || song.title) && (
            <button
              onClick={() => setSong({ title: "", artist: "", url: "" })}
              style={{
                alignSelf: "flex-start",
                background: "none", border: "none", padding: "4px 2px",
                color: T.textMuted, fontSize: "0.72rem", fontWeight: 600,
                cursor: "pointer", textDecoration: "underline",
              }}
            >
              Clear track
            </button>
          )}
        </div>

        {/* Save button */}
        <button
          onClick={() => onSave({ beamColors: colors, genre, nowPlaying: song })}
          disabled={saving}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 14,
            background: "linear-gradient(135deg, rgba(180,79,255,0.4), rgba(255,60,172,0.4))",
            border: "1px solid rgba(180,79,255,0.5)",
            color: T.textPrimary, fontWeight: 700, fontSize: "0.9rem",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1, marginBottom: 12,
          }}
        >
          {saving ? "Saving..." : "Save Stage Config"}
        </button>

      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function UserDashboard() {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const [activeView, setActiveView]           = useState(
    routerLocation.state?.tab === "profile" ? "profile" : "collection"
  );
  const [selectedKandi, setSelectedKandi]     = useState(null);
  const [editingKandi, setEditingKandi]       = useState(null);
  const [savingEdit, setSavingEdit]           = useState(false);
  const [editError, setEditError]             = useState("");
  const [user, setUser]                       = useState(null);
  const [kandiCollection, setKandiCollection] = useState([]);
  const [friendCount, setFriendCount]         = useState(0);
  const [friends, setFriends]                 = useState([]);
  const [activityFeed, setActivityFeed]       = useState([]);
  const [feedHasMore, setFeedHasMore]         = useState(false);
  const [feedCursor, setFeedCursor]           = useState(null);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);
  const [photos, setPhotos]                   = useState([]);
  const [showUpload, setShowUpload]           = useState(false);
  const [uploading, setUploading]             = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [loadError, setLoadError]             = useState(false);
  const [kandiHistory, setKandiHistory]       = useState([]);
  const [showHistory, setShowHistory]         = useState(false);
  const [stageConfig, setStageConfig]         = useState(null);
  const [showStageSettings, setShowStageSettings] = useState(false);
  const [savingStage, setSavingStage]         = useState(false);
  const [privacy, setPrivacy]                 = useState(null);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "";

  // Keep the active tab in sync with bottom-nav taps while already on /dashboard
  useEffect(() => {
    const tab = routerLocation.state?.tab;
    if (tab === "profile") setActiveView("profile");
    else if (tab === "collection") setActiveView("collection");
  }, [routerLocation.state]);

  const fetchData = useCallback(async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        if (!userData) { window.location.href = "/login"; return; }
        setUser(userData);

        // viewerId marks these as own-profile requests so privacy gates pass
        const me = `viewerId=${userData.id}`;
        const [kandiResponse, friendsResponse, photosResponse, stageResponse, historyResponse, feedResponse, privacyResponse] = await Promise.all([
          axios.get(`${API_URL}/api/user/${userData.id}/kandi?${me}`),
          axios.get(`${API_URL}/api/friends/${userData.id}`).catch(() => ({ data: { friends: [] } })),
          axios.get(`${API_URL}/api/user/${userData.id}/photos?${me}`).catch(() => ({ data: { photos: [] } })),
          axios.get(`${API_URL}/api/user/${userData.id}/stage-config`).catch(() => ({ data: { stageConfig: null } })),
          axios.get(`${API_URL}/api/user/${userData.id}/kandi/history`).catch(() => ({ data: { kandis: [] } })),
          axios.get(`${API_URL}/api/friends/${userData.id}/feed?${me}&limit=6`).catch(() => ({ data: { activities: [] } })),
          axios.get(`${API_URL}/api/user/${userData.id}/privacy?${me}`).catch(() => ({ data: { privacy: null } })),
        ]);
        if (kandiResponse.data.success) setKandiCollection(kandiResponse.data.kandis || []);
        setFriendCount(friendsResponse.data.friends?.length || 0);
        setFriends(friendsResponse.data.friends || []);
        setPhotos(photosResponse.data.photos || []);
        setStageConfig(stageResponse.data.stageConfig || null);
        setKandiHistory(historyResponse.data.kandis || []);
        setActivityFeed(feedResponse.data.activities || []);
        setFeedHasMore(!!feedResponse.data.hasMore);
        setFeedCursor(feedResponse.data.nextCursor || null);
        setPrivacy(privacyResponse.data.privacy || null);

      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Collection grid entrance — a fast cascade, cleared on completion so
  // hover transforms take back over. Skipped under prefers-reduced-motion.
  useEffect(() => {
    if (loading || activeView !== "collection" || prefersReducedMotion()) return;
    const cards = document.querySelectorAll(".kandi-card");
    if (!cards.length) return;
    cards.forEach((c) => { c.style.transition = "none"; });
    animate(cards, {
      opacity: [0, 1],
      translateY: [14, 0],
      duration: 340,
      delay: stagger(45),
      ease: "out(3)",
      onComplete: () => cards.forEach((c) => {
        c.style.transition = "";
        c.style.transform = "";
        c.style.opacity = "";
      }),
    });
  }, [loading, activeView]);

  const handleKandiClick = async (kandi) => {
    // Open instantly with what the grid already knows (own-collection kandi
    // carry full history) — no dead tap on festival networks. The fetch then
    // hydrates with the freshest journey; if it fails, the grid data stands.
    setSelectedKandi(kandi);
    try {
      const response = await axios.get(`${API_URL}/api/kandi/${kandi.id}`);
      setSelectedKandi((cur) => (cur && cur.id === kandi.id ? response.data : cur));
    } catch (err) {
      console.error("Error fetching kandi details:", err);
    }
  };

  const handleSaveBeads = async (beads) => {
    if (!editingKandi) return;
    setSavingEdit(true);
    setEditError("");
    try {
      const savedId = editingKandi.id;
      await axios.put(`${API_URL}/api/kandi/${savedId}`, {
        colors: beads.map((b) => b.color),
        shapes: beads.map((b) => b.shape),
        userId: user.id,
      });
      // Refresh collection to reflect new bead data
      const kandiResponse = await axios.get(`${API_URL}/api/user/${user.id}/kandi?viewerId=${user.id}`);
      if (kandiResponse.data.success) setKandiCollection(kandiResponse.data.kandis || []);
      setEditingKandi(null);
      // Success made visible: the saved card pulses its glow
      pulseCard(savedId);
    } catch (err) {
      // Dev diagnostics stay in the console; the modal shows human copy
      console.error("Error saving beads:", err);
      setEditError("Couldn't save your beads — check your connection and try again ✌️");
    } finally {
      setSavingEdit(false);
    }
  };

  const closeModal  = () => setSelectedKandi(null);
  const closeEditor = () => { setEditingKandi(null); setEditError(""); };

  const handlePhotoUpload = async ({ file, message, kandiId }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("userId", user.id);
      if (message) formData.append("message", message);
      if (kandiId) formData.append("kandiId", kandiId);

      const res = await axios.post(`${API_URL}/api/photos/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        setPhotos(prev => [res.data.photo, ...prev]);
        setShowUpload(false);
      }
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleLoadMoreFeed = async () => {
    if (!user?.id || !feedCursor || feedLoadingMore) return;
    setFeedLoadingMore(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/friends/${user.id}/feed?viewerId=${user.id}&limit=20&before=${encodeURIComponent(feedCursor)}`
      );
      setActivityFeed(prev => [...prev, ...(res.data.activities || [])]);
      setFeedHasMore(!!res.data.hasMore);
      setFeedCursor(res.data.nextCursor || null);
    } catch (err) {
      console.error("Load more feed error:", err);
    } finally {
      setFeedLoadingMore(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/photos/${photoId}`, { data: { userId: user.id } });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (err) {
      console.error("Delete photo error:", err);
    }
  };

  const handleSaveStageConfig = async (cfg) => {
    setSavingStage(true);
    try {
      const res = await axios.put(`${API_URL}/api/user/${user.id}/stage-config`, { ...cfg, viewerId: user.id });
      if (res.data.success) setStageConfig(res.data.stageConfig);
      setShowStageSettings(false);
    } catch (err) {
      console.error("Save stage config error:", err);
    } finally {
      setSavingStage(false);
    }
  };

  const rigBeams = useMemo(
    () => buildRigBeams(stageConfig?.beamColors, stageConfig?.genre),
    [stageConfig?.beamColors, stageConfig?.genre]
  );

  // Genre-colored avatar ring; null → rainbow cycle fallback
  const avatarGlowColor = GENRE_GLOW[stageConfig?.genre] || null;

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: T.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ textAlign: "center" }}>
          <div className="animate-neon-pulse" style={{
            width: 52, height: 52, borderRadius: "50%",
            margin: "0 auto 16px",
            background: `linear-gradient(135deg, ${T.purple}, ${T.cyan})`,
          }} />
          <p style={{ color: T.textMuted, fontSize: "0.9rem", margin: 0 }}>Loading your collection…</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Prep data for BraceletProfileCard
  const modalKandi = selectedKandi ? {
    ...selectedKandi,
    ownerCount:   uniqueOwnerCount(selectedKandi.history),
    scannedDate:  selectedKandi.createdAt
      ? new Date(selectedKandi.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "—",
    currentOwner: selectedKandi.history?.[0]?.username,
  } : null;

  const modalHistory = (selectedKandi?.history || []).map((h, i) => ({
    username:  h.username || h.user || "Unknown",
    date:      new Date(h.date || h.timestamp || Date.now()).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    }),
    location:  h.location || "Unknown location",
    isCurrent: i === 0,
  }));

  const totalScans = kandiCollection.reduce((s, k) => s + (k.scanCount || 0), 0);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="dashboard-root" style={{ minHeight: "100vh", background: T.bg, padding: "80px 16px 56px" }}>

      {/* Ambient glows */}
      <div style={{
        position: "fixed", top: "15%", left: "5%", width: 440, height: 440,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(180,79,255,0.06) 0%, transparent 70%)",
      }} />
      <div style={{
        position: "fixed", bottom: "10%", right: "5%", width: 380, height: 380,
        borderRadius: "50%", pointerEvents: "none",
        background: "radial-gradient(circle, rgba(0,245,255,0.05) 0%, transparent 70%)",
      }} />

      <div style={{ maxWidth: 920, margin: "0 auto" }}>

        {/* View switching happens via the bottom nav (mobile) and the
            Collection / My Stage links in the top navbar (desktop). */}

        {/* ── COLLECTION VIEW ────────────────────────────────────────────── */}
        {activeView === "collection" && (
          <div>
            <div style={{ marginBottom: 28 }}>
              <h1 style={{
                margin: "0 0 8px",
                fontFamily: "var(--font-display)",
                fontSize: "clamp(1.5rem, 4vw, 2rem)", fontWeight: 700,
                color: T.pink,
                textShadow: `0 0 24px ${T.purple}60`,
              }}>
                My Kandi Collection
              </h1>
              <p style={{ color: T.textStrong, margin: 0, fontSize: "0.9rem" }}>
                {kandiCollection.length}{" "}
                {kandiCollection.length === 1 ? "piece" : "pieces"} of PLUR
                spreading love across the rave community
              </p>
            </div>

            {loadError && kandiCollection.length === 0 ? (
              /* A failed load is an error, never a fake empty collection */
              <div role="alert" style={{
                textAlign: "center", padding: "48px 32px",
                background: `${T.red}0f`,
                border: `1px solid ${T.red}40`, borderRadius: 24,
              }}>
                <div style={{ fontSize: "2.6rem", marginBottom: 14 }}>📡</div>
                <p style={{ color: T.textPrimary, fontWeight: 700, fontSize: "1.05rem", margin: "0 0 8px" }}>
                  Couldn't load your collection
                </p>
                <p style={{ color: T.textStrong, margin: "0 0 24px", fontSize: "0.9rem" }}>
                  The connection dropped — your kandi are safe. Try again?
                </p>
                <button
                  onClick={fetchData}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "12px 28px", minHeight: 44, border: "none",
                    background: "linear-gradient(135deg, #ff3cac, #b44fff)",
                    color: "#fff", cursor: "pointer",
                    borderRadius: 50, fontWeight: 700, fontSize: "0.9rem",
                    boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
                  }}
                >
                  Retry
                </button>
              </div>
            ) : kandiCollection.length === 0 ? (
              <div style={{
                textAlign: "center", padding: "60px 32px",
                background: T.card, backdropFilter: "blur(20px)",
                border: T.border, borderRadius: 24,
              }}>
                <div aria-hidden style={{
                  width: 56, height: 56, borderRadius: "50%", margin: "0 auto 20px",
                  border: "2px dashed rgba(255,255,255,0.18)",
                }} />
                <p style={{ color: T.textPrimary, fontWeight: 700, fontSize: "1.05rem", margin: "0 0 8px" }}>
                  No kandi yet!
                </p>
                <p style={{ color: T.textStrong, margin: "0 0 28px", fontSize: "0.9rem" }}>
                  Scan your first NFC tag to start your collection
                </p>
                <button
                  onClick={() => navigate("/scan")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "12px 28px", minHeight: 44, border: "none",
                    background: "linear-gradient(135deg, #ff3cac, #b44fff)",
                    color: "#fff", cursor: "pointer",
                    borderRadius: 50, fontWeight: 700, fontSize: "0.9rem",
                    boxShadow: "0 4px 20px rgba(180,79,255,0.35)",
                  }}
                >
                  <Zap size={15} /> Scan Your First Kandi
                </button>
              </div>
            ) : (
              <div className="kandi-grid" style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(255px, 1fr))",
                gap: 14,
              }}>
                {kandiCollection.map((kandi) => (
                  <KandiCard
                    key={kandi.id}
                    kandi={kandi}
                    onClick={handleKandiClick}
                    onEdit={(k) => { setEditingKandi(k); setEditError(""); }}
                  />
                ))}
              </div>
            )}

            {/* Previously Traded */}
            {kandiHistory.length > 0 && (
              <div style={{ marginTop: 40 }}>
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  aria-expanded={showHistory}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    background: "none", border: "none", cursor: "pointer",
                    padding: 0, minHeight: 44, marginBottom: showHistory ? 16 : 0,
                  }}
                >
                  {/* Solid Headliner Gold — gold already means "memory/history"
                      per the Signal Rule; no second gradient dialect */}
                  <h2 style={{
                    margin: 0, fontSize: "1.1rem", fontWeight: 800, color: T.gold,
                  }}>
                    Previously Traded
                  </h2>
                  <span style={{
                    fontSize: "0.75rem", fontWeight: 700, color: T.textMuted,
                    background: "rgba(255,255,255,0.06)", padding: "3px 10px",
                    borderRadius: 20,
                  }}>
                    {kandiHistory.length}
                  </span>
                  <ChevronRight size={16} color={T.textMuted} style={{
                    marginLeft: "auto",
                    transform: showHistory ? "rotate(90deg)" : "rotate(0)",
                    transition: "transform 0.2s",
                  }} />
                </button>
                <p style={{ color: T.textStrong, fontSize: "0.82rem", margin: "4px 0 16px" }}>
                  Kandi you've scanned, owned, or traded — see where they've traveled
                </p>

                {showHistory && (
                  <div className="kandi-grid" style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(255px, 1fr))",
                    gap: 14,
                  }}>
                    {kandiHistory.map((kandi) => (
                      <div
                        key={kandi.id}
                        role="button"
                        tabIndex={0}
                        aria-label={`View ${kandi.name}`}
                        onClick={() => handleKandiClick(kandi)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleKandiClick(kandi); }
                        }}
                        style={{
                          background: T.card,
                          backdropFilter: "blur(20px)",
                          WebkitBackdropFilter: "blur(20px)",
                          border: T.border,
                          borderRadius: T.radius,
                          padding: "18px 16px 16px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          // Dimmed as "memory vs present" but readable at rest —
                          // touch users never get the hover brighten
                          opacity: 0.85,
                          position: "relative",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(0)"; }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                          <span style={{ fontSize: "1.4rem" }}>{kandi.emoji}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                              margin: 0, color: T.textPrimary, fontWeight: 700,
                              fontSize: kandiNameSize(kandi.name),
                              lineHeight: 1.25, wordBreak: "break-word",
                            }}>
                              {kandi.name}
                            </p>
                            <p style={{ margin: "2px 0 0", color: T.textMuted, fontSize: "0.72rem" }}>
                              {kandi.scanCount} scans · Now with {kandi.currentOwner?.username || "someone"}
                            </p>
                          </div>
                        </div>
                        <MiniBeads colors={kandi.colors || []} shapes={kandi.shapes || []} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── MY STAGE VIEW ─────────────────────────────────────────────── */}
        {activeView === "profile" && (
          <div className="stage-root" style={{ maxWidth: 980, margin: "0 auto", position: "relative", paddingTop: 36 }}>

            {/* Truss + moving-head lighting rig behind everything */}
            <StageRig beams={rigBeams} />

            {/* Stage controls — privacy + gear + logout */}
            <div style={{ position: "absolute", top: 44, right: 0, zIndex: 5, display: "flex", gap: 8 }}>
              <button
                onClick={() => setShowPrivacySettings(true)}
                title="Privacy settings"
                style={{
                  width: 44, height: 44, borderRadius: "50%", cursor: "pointer",
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,245,255,0.15)"; e.currentTarget.style.borderColor = "rgba(0,245,255,0.4)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
              >
                <Shield size={15} color={T.textMuted} />
              </button>
              <button
                onClick={() => setShowStageSettings(true)}
                className="stage-gear-btn"
                style={{
                  width: 44, height: 44, borderRadius: "50%", cursor: "pointer",
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(180,79,255,0.2)"; e.currentTarget.style.borderColor = "rgba(180,79,255,0.4)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; }}
              >
                <Settings size={15} color={T.textMuted} />
              </button>
              <button
                className="stage-logout-btn"
                onClick={() => { localStorage.removeItem("user"); navigate("/login"); }}
                title="Log out"
                style={{
                  width: 44, height: 44, borderRadius: "50%", cursor: "pointer",
                  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                  display: "none", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s",
                }}
              >
                <LogOut size={15} color={T.textMuted} />
              </button>
            </div>

            {/* ── Stage Header ── */}
            <StageHeader />

            {/* ── DJ deck — full-width centerpiece of the stage ── */}
            <div className="dj-deck-wrapper" style={{ position: "relative", zIndex: 2, marginBottom: 14 }}>
              <DJDeck
                user={user}
                kandiCount={kandiCollection.length}
                totalScans={totalScans}
                friendCount={friendCount}
                collection={kandiCollection}
                onKandiClick={handleKandiClick}
                glowColor={avatarGlowColor}
              />
            </div>

            {/* ── 2-column stage layout: stacks on mobile (see .stage-columns) ── */}
            <div className="stage-columns">

              {/* Left column — PLUR meter, orbit */}
              <div className="stage-col">
                <NowPlayingCard
                  nowPlaying={stageConfig?.nowPlaying}
                  onEdit={() => setShowStageSettings(true)}
                />

                <div className="kandi-orbit-card" style={{
                  background: T.card, backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: T.border, borderRadius: 24,
                  padding: "22px 16px",
                  position: "relative", zIndex: 2,
                }}>
                  <KandiOrbit
                    collection={kandiCollection}
                    user={user}
                    onKandiClick={handleKandiClick}
                    glowColor={avatarGlowColor}
                  />
                </div>
              </div>

              {/* Right column — activity feed, photos, passport */}
              <div className="stage-col">
                <StageActivityFeed
                  feed={activityFeed}
                  apiUrl={API_URL}
                  viewerId={user.id}
                  friends={friends}
                  hasMore={feedHasMore}
                  loadingMore={feedLoadingMore}
                  onLoadMore={handleLoadMoreFeed}
                />

                <div style={{
                  background: T.card, backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: T.border, borderRadius: 24,
                  padding: "22px 22px",
                  position: "relative", zIndex: 2,
                }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Camera size={14} color={T.pink} style={{ filter: `drop-shadow(0 0 6px ${T.pink})` }} />
                      <Label>Backstage Pics</Label>
                    </div>
                    <button
                      onClick={() => setShowUpload(true)}
                      style={{
                        display: "flex", alignItems: "center", gap: 5,
                        padding: "8px 14px", borderRadius: 10, minHeight: 36,
                        background: "linear-gradient(135deg, rgba(255,60,172,0.18), rgba(180,79,255,0.18))",
                        border: "1px solid rgba(255,60,172,0.35)",
                        color: T.pink, fontSize: "0.75rem", fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      <Plus size={12} /> Add Photo
                    </button>
                  </div>
                  <Divider />
                  <PhotoGallery
                    photos={photos}
                    apiUrl={API_URL}
                    onDelete={handleDeletePhoto}
                    userId={user.id}
                    friends={friends}
                  />
                </div>

                <div style={{ position: "relative", zIndex: 2 }}>
                  <FestivalPassport collection={kandiCollection} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Upload photo modal ─────────────────────────────────────────────── */}
      {showUpload && (
        <UploadPhotoModal
          collection={kandiCollection}
          friends={friends}
          onUpload={handlePhotoUpload}
          onClose={() => setShowUpload(false)}
          uploading={uploading}
        />
      )}

      {/* ── Kandi detail modal ──────────────────────────────────────────────── */}
      {selectedKandi && modalKandi && (
        <BraceletProfileCard
          kandi={modalKandi}
          history={modalHistory}
          onClose={closeModal}
          onEdit={() => {
            const k = selectedKandi;
            setSelectedKandi(null);
            setEditingKandi({ id: k.id, name: k.name, colors: k.colors, shapes: k.shapes });
            setEditError("");
          }}
        />
      )}

      {/* ── Edit bead modal ─────────────────────────────────────────────────── */}
      {editingKandi && (
        <EditKandiModal
          kandi={editingKandi}
          onSave={handleSaveBeads}
          onClose={closeEditor}
          saving={savingEdit}
          error={editError}
        />
      )}

      {/* ── Stage customizer modal ─────────────────────────────────────────── */}
      {showStageSettings && (
        <StageCustomizer
          stageConfig={stageConfig}
          onSave={handleSaveStageConfig}
          onClose={() => setShowStageSettings(false)}
          saving={savingStage}
        />
      )}

      {/* ── Privacy settings modal ─────────────────────────────────────────── */}
      {showPrivacySettings && (
        <PrivacySettingsModal
          userId={user.id}
          username={user.username}
          initial={privacy}
          onSaved={(p) => setPrivacy(p)}
          onClose={() => setShowPrivacySettings(false)}
        />
      )}
    </div>
  );
}

// Shared stage/collection building blocks — reused by the public profile page
// (/user/:username) to render a read-only version of someone's stage.
export {
  StageRig,
  buildRigBeams,
  StageHeader,
  DJDeck,
  NowPlayingCard,
  KandiOrbit,
  FestivalPassport,
  PhotoGallery,
  KandiCard,
  Label,
  Divider,
  GENRE_GLOW,
};

export default UserDashboard;
